import React, { useEffect, useState, useRef } from 'react';

import {
    Box,
    Stack,
    Typography,
    Paper,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListSubheader,
    ListItemButton,
    ListItemText,
    ListItem,
    Grid,
    Skeleton,
    FormControl,
    styled
} from '@mui/material';

import { useSystemPrompt } from '../../hooks/useSystemPrompt';
import { useUsers, userTypes } from '../../hooks/api/useUsers';
import { CommonIcons, icons } from '../common/CommonIcons';
import { routeTypes } from '../../hooks/useSystemRoutes';
import { useAuthAccess } from '../../hooks/useAuthAccess';
import { useTranslations } from '../../hooks/useTranslations';

const StyledTitle = styled(Box)(({ theme }) => ({
    fontWeight: 800,
    fontSize: '1.0rem'
}))

const StyledSubTitle = styled(Box)(({ theme }) => ({
    fontWeight: 300,
    fontSize: '0.8rem'
}))

export default function SearchSuppliers(
    {
        routeType,
        id,
        handleOnCancel,
        handleOnSelect,
        textProps
    }
) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const searchInputRef = useRef()

    const [open, setOpen] = useState(false)
    const [keyword, setKeyword] = useState("")
    const [data, setData] = useState([])

    const { authData } = useAuthAccess()

    const { promptMessage } = useSystemPrompt()
    const { isLoading, list, filterOperatorTypes } = useUsers({ userType: userTypes.supplier });

    const fetchData = async () => {

        try {

            let filterValues = [
                {
                    name: "company_name",
                    operator: filterOperatorTypes.contains,
                    value: keyword,
                    group: "name_group"
                },
                {
                    name: "username",
                    operator: filterOperatorTypes.eqOr,
                    value: keyword,
                    group: "name_group"
                },
                {
                    name: "code",
                    operator: filterOperatorTypes.eqOr,
                    value: keyword,
                    group: "name_group"
                }
            ]

            // add in the route type supplier type
            filterValues.push({
                name: "type",
                operator: filterOperatorTypes.eq,
                value: routeType,
                // group: "name_group"
            })

            // only if the route type is retail - need to filter only the same retailer id supplier
            if (routeType === routeTypes.retail) {
                filterValues.push({
                    name: "retailer_id",
                    operator: filterOperatorTypes.eq,
                    value: authData?.profile?.user?.id,
                    // group: "name_group"
                })
            }

            let props = {
                limit: 30,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: -1,
                },
                filterValue: filterValues
            }

            // props.dataTypes = ["branch","shipping_address"]

            let response = await list(props);
            setData(response?.data?.suppliers ? response?.data?.suppliers : [])

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

    }

    useEffect(() => {

        setData([])

    }, [])

    useEffect(() => {

        if (keyword) {
            fetchData({ keyword });
        } else {
            setKeyword("")
            setData([])
        }

    }, [keyword])

    useEffect(() => {

        const timeout = setTimeout(() => {

            if (open) {
                searchInputRef?.current.focus();
                // console.log('serachRef', searchInputRef?.current)
            }

        }, 100);

        return () => {
            clearTimeout(timeout);
        };

    }, [open])

    const RenderListData = () => {

        if (isLoading) {
            return (
                [1, 2, 3, 4].map((item, index) => <RenderListDataItem key={index} isLoading={Boolean(true)} />)
            )
        }

        return (
            data.map((item, index) =>
                <RenderListDataItem key={index} item={item} index={index} />
            )
        )
    }

    const RenderListDataItemSkeleton = () => {
        return (
            <>
                <Skeleton
                    animation="wave"
                    // variant="circular" 
                    sx={{
                        minWidth: 100,
                        mr: 1,
                        height: 50
                    }}
                />
                <ListItemText
                // fullWidth
                // height={50}
                >
                    <Skeleton
                        variant="rectangular"
                        width={"100%"}
                        height={20}
                    />
                    <Skeleton
                        variant="rectangular"
                        width={"100%"}
                        height={20}
                        sx={{
                            mt: 1
                        }}
                    />
                </ListItemText>
            </>
        )
    }

    const RenderListDataItemDisplay = ({ item }) => {

        // console.log('item', item)

        return (<>
            <Grid container padding={1}>
                <Grid item md={1} textAlign={"center"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                    <CommonIcons name={icons.users} sx={{ height: 30, width: 30 }} />
                </Grid>
                <Grid item md={7}>
                    <StyledTitle>
                        {`${item?.company_name} (${item?.code})`}
                    </StyledTitle>
                    <StyledSubTitle>
                        {`${item?.address_1} ${item?.address_2}`}
                    </StyledSubTitle>
                    <StyledSubTitle>
                        {`${item?.postcode} ${item?.area_name}  ${item?.state_name}, ${item?.country_name}`}
                    </StyledSubTitle>
                </Grid>
                <Grid item md={4} textAlign={"right"}>
                    <Stack
                        direction={"column"}
                        justifyContent={"flex-end"}
                    >
                        <Typography>
                            {`${item?.contact}`}
                        </Typography>
                        <StyledSubTitle sx={{ ml: 1 }}>
                            {item?.email}
                        </StyledSubTitle>
                    </Stack>
                </Grid>
            </Grid>
        </>
        )
    }

    const RenderListDataItem = ({ isLoading = false, index, item }) => {
        return (
            <ListItem
                key={index}
                disablePadding
                divider
            >
                <ListItemButton
                    role={undefined}
                    onClick={() => {
                        // handleOnCancel();
                        handleSelect({ item, id: item?.id });
                    }}
                    dense
                >
                    {
                        Boolean(isLoading) ? <RenderListDataItemSkeleton /> : <RenderListDataItemDisplay item={item} />

                    }

                </ListItemButton>
            </ListItem>
        )
    }

    const RenderList = () => {
        return (
            <List
                // sx={{ width: '100%', maxWidth: 360,
                //     bgcolor: 'background.paper' }}
                component="nav"
                aria-labelledby="nested-list-subheader"
                subheader={
                    <ListSubheader
                        component="div"
                        id="nested-list-subheader"
                        sx={{
                            // backgroundColor: 'red'
                            // pl: 1,
                            // pr: 1
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                p: 1
                            }}
                        >
                            {/* <FormControl variant="standard"> */}
                            <TextField
                                inputRef={searchInputRef}
                                autoComplete="off"
                                margin="dense"
                                size="small"
                                label={t("searchKeyword")}
                                fullWidth
                                variant="outlined"
                                onChange={e => {
                                    setKeyword(e.target.value)
                                }}
                                value={keyword}
                                InputProps={{
                                    startAdornment:
                                        <InputAdornment position="start">
                                            <CommonIcons name={icons.search} />
                                        </InputAdornment>
                                }}
                            />
                            {/* </FormControl> */}
                        </Paper>
                    </ListSubheader>
                }
            >
                {
                    RenderListData()
                }

            </List>
        )
    }

    const handleExit = () => {
        setOpen(false);

        if (handleOnCancel)
            handleOnCancel();
    }

    const handleSelect = ({ id, item }) => {
        setOpen(false);

        if (handleOnSelect)
            handleOnSelect({ id, item })

        promptMessage(true, t(`selected1Item`))
    }

    const RenderSearch = () => {
        return (
            <TextField
                // disabled
                disabled={textProps?.disabled ? textProps.disabled : false}
                margin="normal"
                size="small"
                fullWidth
                name={id}
                label={t("selectSupplier")}
                type="text"
                variant="outlined"
                value={textProps?.value ? textProps.value : ""}
                // onChange={handleChange}
                error={textProps?.error ? textProps.error : false}
                helperText={textProps?.helperText}
                InputProps={{
                    sx: {
                        pr: 0.5
                    },
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                sx={{
                                    m: 0,
                                    // p: 0
                                }}
                                aria-label="open"
                                onClick={() => setOpen(true)}
                            >
                                <CommonIcons name={icons.search} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        )
    }

    return (
        <Box>

            {RenderSearch()}

            {/* {JSON.stringify(data)} */}

            <Dialog
                sx={{
                    '& .MuiDialog-paper':
                    {
                        // width: '80%', 
                        minHeight: '90vh'
                    }
                }}
                fullWidth
                maxWidth="md"
                // TransitionProps={{ onEntering: handleEntering }}
                open={open}
                // {...other}
                onClose={handleExit}
            >
                <DialogTitle
                >
                    {t('searchSupplier')}

                    <IconButton
                        aria-label="close"
                        onClick={handleExit}
                        sx={{
                            position: 'absolute',
                            right: 10,
                            top: 10,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CommonIcons name={icons.close} />
                    </IconButton>

                </DialogTitle>
                <DialogContent
                    dividers
                    // minHeight="100%"
                    sx={{
                        // backgroundColor: 'red',
                        pl: 0,
                        pr: 0
                    }}
                >

                    {
                        RenderList()
                    }

                </DialogContent>
                {/* <DialogActions>
                    <Button 
                        autoFocus 
                        // onClick={handleCancel}
                        onClick={() => {
                            handleOnCancel()
                        }}
                    >Cancel</Button>
                    <Button 
                        // onClick={handleOk}
                        onClick={() => {
                            handleOnConfirm()
                        }}
                    >Ok</Button>
                </DialogActions> */}
            </Dialog>
        </Box>
    )
}